'use client';

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { INVESTMENT_SECTORS_PATH } from '@/lib/site';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Search,
  X,
} from 'lucide-react';

import { formatPrice } from '@/lib/format-quote';

const DEFAULT_PER_PAGE = 10;
const PER_PAGE_OPTIONS = [10, 25, 50];

function parsePerPageParam(raw) {
  const n = parseInt(String(raw ?? ''), 10);
  return PER_PAGE_OPTIONS.includes(n) ? n : DEFAULT_PER_PAGE;
}

const DIRECTORY_SORTABLE = [
  { id: 'name', label: 'Partner' },
  { id: 'region', label: 'Region' },
  { id: 'type', label: 'Type' },
  { id: 'founded', label: 'Founded' },
  { id: 'status', label: 'Status' },
];

const TICKER_SORTABLE = [
  { id: 'name', label: 'Company' },
  { id: 'region', label: 'Location' },
  { id: 'ticker', label: 'Ticker' },
  { id: 'quoteChange', label: 'Change' },
  { id: 'marketCapSort', label: 'Mkt cap' },
];

/**
 * Open / Prev close columns inserted after Ticker for the ticker preset. They use
 * each row's trading currency (HKD/KRW/EUR/etc.) — cross-currency price sort
 * is meaningless, so they're rendered as static (non-sortable) headers.
 */
const TICKER_PRICE_HEADERS = [
  { id: 'priceOpen', label: 'Open' },
  { id: 'priceClose', label: 'Prev close' },
];

const STATUS_BADGE = {
  Active: 'border border-green-500/30 bg-green-500/10 text-green-400',
  Onboarding: 'border border-amber-500/30 bg-amber-500/10 text-amber-300',
  Pending: 'border border-border bg-bg-secondary text-text-muted',
  Private: 'border border-sky-500/30 bg-sky-500/10 text-sky-300',
  Government: 'border border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
  Other: 'border border-slate-500/30 bg-slate-500/10 text-slate-300',
  Acquired: 'border border-purple-500/30 bg-purple-500/10 text-purple-300',
};

function uniqueSorted(rows, key) {
  const set = new Set();
  for (const row of rows) {
    const v = String(row?.[key] || '').trim();
    if (v) set.add(v);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function compareValues(a, b, dir) {
  const sign = dir === 'desc' ? -1 : 1;
  if (typeof a === 'number' && typeof b === 'number' && !Number.isNaN(a) && !Number.isNaN(b)) {
    return (a - b) * sign;
  }
  return String(a ?? '').localeCompare(String(b ?? '')) * sign;
}

function regionMatches(row, regionFilter, knownRegions) {
  if (regionFilter === 'all') return true;
  if (knownRegions.includes(regionFilter)) return row.region === regionFilter;
  return String(row.region || '')
    .toLowerCase()
    .includes(String(regionFilter).toLowerCase());
}

function sortableIds(preset) {
  const cols = preset === 'ticker' ? TICKER_SORTABLE : DIRECTORY_SORTABLE;
  return new Set(cols.map((c) => c.id));
}

function SortHeader({ column, sortBy, sortDir, onSort }) {
  const active = sortBy === column.id;
  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th scope="col" className="px-4 py-3 select-none">
      <button
        type="button"
        onClick={() => onSort(column.id)}
        className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider transition-colors ${
          active ? 'text-accent-amber' : 'text-text-muted hover:text-text-primary'
        }`}
      >
        {column.label}
        <Icon size={12} aria-hidden />
      </button>
    </th>
  );
}

function applySortDirToQuery(out, sortBy, sortDir) {
  if (sortBy !== 'name' || sortDir !== 'asc') {
    out.sort = sortBy;
    out.dir = sortDir;
  }
}

const NON_QUOTE_TICKERS = new Set([
  'Private',
  'Government Owned',
  'Crown Corporation',
  'Organization',
  'N/A',
  '',
]);

/**
 * Merge a quotes map (keyed by ticker) into partner rows.
 *
 * - Rows whose `status` is intrinsically non-quotable (Private / Government /
 *   Other / Acquired) keep their JSON values verbatim — they're not supposed
 *   to have live market data.
 * - Rows with a public ticker whose live quote came back: replace change,
 *   marketCap, and add open/close prices in trading currency.
 * - Rows with a public ticker whose live quote is missing (the handful of
 *   exchanges Yahoo doesn't cover, e.g. Dhaka / ADX / JSE): show `—`
 *   placeholders rather than "Private" — they ARE public, we just don't
 *   currently have free data for them.
 */
const NON_QUOTE_STATUSES = new Set(['Acquired', 'Government', 'Other', 'Private']);
const PLACEHOLDER = '—';

function applyQuotes(partners, quotes) {
  if (!quotes) return partners;
  return partners.map((row) => {
    if (NON_QUOTE_STATUSES.has(row?.status)) return row;
    const ticker = String(row?.ticker || '').trim();
    if (!ticker || NON_QUOTE_TICKERS.has(ticker)) return row;
    const q = quotes[ticker];
    if (!q || (!q.change && !q.marketCap)) {
      return {
        ...row,
        quoteChange: PLACEHOLDER,
        marketCap: PLACEHOLDER,
        marketCapSort: -1,
        priceOpen: null,
        priceClose: null,
        priceCurrency: null,
      };
    }
    return {
      ...row,
      quoteChange: q.change || PLACEHOLDER,
      marketCap: q.marketCap || PLACEHOLDER,
      marketCapSort: Number.isFinite(q.marketCapBillions) && q.marketCapBillions > 0
        ? q.marketCapBillions
        : -1,
      priceOpen: Number.isFinite(q.priceOpen) ? q.priceOpen : null,
      priceClose: Number.isFinite(q.priceClose) ? q.priceClose : null,
      priceCurrency: q.currency || null,
    };
  });
}

export default function PartnersTable({
  partners = [],
  quotes = null,
  emptyHint = null,
  preset = 'directory',
  syncUrl = false,
  /** Current global tab id (matches this panel when visible). */
  urlTab = '',
  /** This category id — must equal `urlTab` when this panel is mounted with sync. */
  tabId = '',
  listPath = INVESTMENT_SECTORS_PATH,
}) {
  const router = useRouter();
  const urlSyncActive = syncUrl && router.isReady && urlTab === tabId;

  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('all');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const skipUrlWrite = useRef(false);

  const sortableSet = useMemo(() => sortableIds(preset), [preset]);
  const sortColumns = preset === 'ticker' ? TICKER_SORTABLE : DIRECTORY_SORTABLE;

  const rows = useMemo(() => applyQuotes(partners, quotes), [partners, quotes]);

  const regions = useMemo(() => uniqueSorted(rows, 'region'), [rows]);
  const types = useMemo(() => uniqueSorted(rows, 'type'), [rows]);
  const statuses = useMemo(() => uniqueSorted(rows, 'status'), [rows]);

  const regionOptions = useMemo(() => {
    if (region !== 'all' && !regions.includes(region)) {
      return [...regions, region].sort((a, b) => a.localeCompare(b));
    }
    return regions;
  }, [regions, region]);

  useEffect(() => {
    if (!urlSyncActive) return;
    const search = router.asPath.includes('?') ? router.asPath.split('?')[1] : '';
    const sp = new URLSearchParams(search);
    const qRaw = sp.get('q');
    const regionRaw = sp.get('region');
    const typeRaw = sp.get('type');
    const statusRaw = sp.get('status');
    const pageRaw = sp.get('page');
    const sizeRaw = sp.get('size');
    const sortRaw = sp.get('sort');
    const dirRaw = sp.get('dir');

    skipUrlWrite.current = true;
    setQuery(qRaw || '');
    setRegion(regionRaw || 'all');
    setType(typeRaw || 'all');
    setStatus(statusRaw || 'all');
    const p = parseInt(String(pageRaw || '1'), 10);
    setPage(Number.isFinite(p) && p > 0 ? p : 1);
    setPerPage(parsePerPageParam(sizeRaw));

    const s = sortRaw && sortableSet.has(sortRaw) ? sortRaw : 'name';
    const d = dirRaw === 'desc' || dirRaw === 'asc' ? dirRaw : 'asc';
    setSortBy(s);
    setSortDir(d);
  }, [urlSyncActive, router.asPath, sortableSet]);

  useEffect(() => {
    if (!urlSyncActive) return;
    if (skipUrlWrite.current) {
      skipUrlWrite.current = false;
      return;
    }

    const out = { tab: urlTab };
    const qt = query.trim();
    if (qt) out.q = qt;
    if (region !== 'all') out.region = region;
    if (type !== 'all') out.type = type;
    if (status !== 'all') out.status = status;
    if (page > 1) out.page = String(page);
    if (perPage !== DEFAULT_PER_PAGE) out.size = String(perPage);
    applySortDirToQuery(out, sortBy, sortDir);

    const cur = { ...router.query };
    const keys = ['tab', 'q', 'region', 'type', 'status', 'page', 'size', 'sort', 'dir'];
    let same = true;
    for (const k of keys) {
      const a = out[k] != null ? String(out[k]) : '';
      const b = cur[k] != null ? String(cur[k]) : '';
      if (a !== b) {
        same = false;
        break;
      }
    }
    if (same) return;

    router.replace({ pathname: listPath, query: out }, undefined, { shallow: true });
  }, [
    urlSyncActive,
    urlTab,
    query,
    region,
    type,
    status,
    page,
    perPage,
    sortBy,
    sortDir,
    router,
    listPath,
  ]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchesQuery = (row) => {
      if (!q) return true;
      if (preset === 'ticker') {
        return (
          String(row.name || '').toLowerCase().includes(q) ||
          String(row.region || '').toLowerCase().includes(q) ||
          String(row.ticker || '').toLowerCase().includes(q) ||
          String(row.quoteChange || '').toLowerCase().includes(q) ||
          String(row.marketCap || '').toLowerCase().includes(q)
        );
      }
      return (
        String(row.name || '').toLowerCase().includes(q) ||
        String(row.region || '').toLowerCase().includes(q) ||
        String(row.type || '').toLowerCase().includes(q) ||
        String(row.description || '').toLowerCase().includes(q)
      );
    };

    return rows
      .filter((row) => {
        if (!regionMatches(row, region, regions)) return false;
        if (type !== 'all' && row.type !== type) return false;
        if (status !== 'all' && row.status !== status) return false;
        if (!matchesQuery(row)) return false;
        return true;
      })
      .sort((a, b) => {
        const va = a?.[sortBy];
        const vb = b?.[sortBy];
        if (preset === 'ticker' && sortBy === 'marketCapSort') {
          const na = Number(va);
          const nb = Number(vb);
          if (Number.isFinite(na) && Number.isFinite(nb)) return (na - nb) * (sortDir === 'desc' ? -1 : 1);
        }
        return compareValues(va, vb, sortDir);
      });
  }, [rows, query, region, type, status, sortBy, sortDir, preset, regions]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [safePage, page]);

  const handleSort = (id) => {
    setPage(1);
    const capCol = preset === 'ticker' ? 'marketCapSort' : 'founded';
    if (sortBy === id) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(id);
      setSortDir(id === capCol ? 'desc' : 'asc');
    }
  };

  const hasAnyFilter =
    query.trim() !== '' || region !== 'all' || type !== 'all' || status !== 'all';

  const clearFilters = () => {
    setQuery('');
    setRegion('all');
    setType('all');
    setStatus('all');
    setPage(1);
  };

  const typeAria = preset === 'ticker' ? 'Filter by listing (public / private)' : 'Filter by type';
  const searchPlaceholder =
    preset === 'ticker'
      ? 'Search name, location, ticker…'
      : 'Search by name, region, type…';

  const tableMinW = preset === 'ticker' ? 'min-w-[1080px]' : 'min-w-[820px]';
  const colSpan = preset === 'ticker' ? 9 : 6;

  return (
    <section className="rounded-2xl border border-border bg-bg-card">
      <div className="flex flex-col gap-3 border-b border-border px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 sm:px-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder={searchPlaceholder}
            className="w-full rounded-lg border border-border bg-bg-secondary py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <select
            value={region}
            onChange={(e) => {
              setRegion(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent-amber focus:outline-none"
            aria-label="Filter by region"
          >
            <option value="all">All regions</option>
            {regionOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent-amber focus:outline-none"
            aria-label={typeAria}
          >
            <option value="all">{preset === 'ticker' ? 'All listings' : 'All types'}</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent-amber focus:outline-none"
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          {hasAnyFilter ? (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 rounded-lg border border-border bg-bg-secondary px-3 py-2 text-xs font-medium text-text-muted transition hover:border-accent-amber/40 hover:text-text-primary"
            >
              <X size={12} aria-hidden /> Clear
            </button>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className={`w-full ${tableMinW}`}>
          <thead className="bg-bg-secondary text-left">
            <tr>
              {sortColumns.map((col, idx) => {
                const header = (
                  <SortHeader
                    key={col.id}
                    column={col}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                );
                // Splice the (static) Open / Prev close columns after Ticker.
                if (preset === 'ticker' && col.id === 'ticker') {
                  return (
                    <Fragment key={col.id}>
                      {header}
                      {TICKER_PRICE_HEADERS.map((p) => (
                        <th
                          key={p.id}
                          scope="col"
                          className="px-4 py-3 text-xs uppercase tracking-wider text-text-muted"
                        >
                          {p.label}
                        </th>
                      ))}
                    </Fragment>
                  );
                }
                return header;
              })}
              {preset === 'ticker' ? (
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs uppercase tracking-wider text-text-muted"
                >
                  Status
                </th>
              ) : null}
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs uppercase tracking-wider text-text-muted"
              >
                Site
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-text-muted">
                  {emptyHint || 'No partners match the current filters.'}
                </td>
              </tr>
            ) : preset === 'ticker' ? (
              pageRows.map((row) => {
                const badgeClass = STATUS_BADGE[row.status] || STATUS_BADGE.Pending;
                return (
                  <tr
                    key={row.id}
                    className="border-t border-border/40 align-top transition-colors hover:bg-bg-secondary/50"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-text-primary">{row.name}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-primary">{row.region}</td>
                    <td className="px-4 py-3 font-mono text-sm text-text-muted">{row.ticker}</td>
                    <td className="px-4 py-3 font-mono text-sm text-text-primary">
                      {row.priceOpen != null
                        ? formatPrice(row.priceOpen, row.priceCurrency) || PLACEHOLDER
                        : PLACEHOLDER}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-text-primary">
                      {row.priceClose != null
                        ? formatPrice(row.priceClose, row.priceCurrency) || PLACEHOLDER
                        : PLACEHOLDER}
                    </td>
                    <td
                      className={`px-4 py-3 font-mono text-sm ${
                        typeof row.quoteChange === 'string' && row.quoteChange.startsWith('+')
                          ? 'text-green-400'
                          : typeof row.quoteChange === 'string' && row.quoteChange.startsWith('-')
                            ? 'text-red-400'
                            : 'text-text-muted'
                      }`}
                    >
                      {row.quoteChange}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-text-primary">{row.marketCap}</td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeClass}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.website ? (
                        <a
                          href={row.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-accent-amber no-underline hover:underline"
                          aria-label={`Open ${row.name} website`}
                        >
                          Visit <ExternalLink size={12} aria-hidden />
                        </a>
                      ) : (
                        <span className="text-xs text-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              pageRows.map((row) => {
                const badgeClass = STATUS_BADGE[row.status] || STATUS_BADGE.Pending;
                return (
                  <tr
                    key={row.id}
                    className="border-t border-border/40 align-top transition-colors hover:bg-bg-secondary/50"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-text-primary">{row.name}</p>
                      {row.description ? (
                        <p className="mt-0.5 text-xs text-text-muted">{row.description}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-primary">{row.region}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="rounded-full border border-border bg-bg-secondary px-2 py-0.5 text-xs text-text-muted">
                        {row.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-text-muted">
                      {Number.isFinite(Number(row.founded)) ? row.founded : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeClass}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.website ? (
                        <a
                          href={row.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-accent-amber no-underline hover:underline"
                          aria-label={`Open ${row.name} website`}
                        >
                          Visit <ExternalLink size={12} aria-hidden />
                        </a>
                      ) : (
                        <span className="text-xs text-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col items-stretch justify-between gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:px-5">
        <p className="text-xs text-text-muted">
          {filtered.length === 0
            ? 'No results'
            : `${(safePage - 1) * perPage + 1}–${Math.min(safePage * perPage, filtered.length)} of ${
                filtered.length
              }`}
          {' · '}Page {safePage} of {totalPages}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-end">
          <label className="flex items-center gap-2 text-xs text-text-muted">
            <span className="whitespace-nowrap">Rows per page</span>
            <select
              value={perPage}
              onChange={(e) => {
                const next = parsePerPageParam(e.target.value);
                setPerPage(next);
                setPage(1);
              }}
              className="rounded-lg border border-border bg-bg-secondary px-2 py-1.5 text-xs text-text-primary focus:border-accent-amber focus:outline-none"
              aria-label="Rows per page"
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 rounded-lg border border-border bg-bg-secondary px-3 py-1.5 text-xs text-text-primary transition hover:border-accent-amber/40 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft size={14} aria-hidden /> Previous
            </button>
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 rounded-lg border border-border bg-bg-secondary px-3 py-1.5 text-xs text-text-primary transition hover:border-accent-amber/40 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next <ChevronRight size={14} aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
